/**
 * Opening a gift-wrapped message.
 *
 * This is one half of a cross-language seam: postrd seals in Go, this reads in
 * TypeScript. A divergence produces mail that is written and cannot be read,
 * which under this architecture is permanent loss — there is no server-side
 * plaintext to fall back on. `seal.test.ts` runs the same fixtures the Go side
 * runs (`testdata/vectors/giftwrap.json`); never change this file without
 * running them.
 */
import type { Event as NostrEvent } from "nostr-tools";
import type { MailSigner } from "./signer";

/** Rumor kind carrying one email. Must match seal.KindMail in Go. */
export const KIND_MAIL = 1314;

/** Where a message landed. Must match the constants in internal/seal. */
export type Disposition = "inbox" | "feed" | "paper_trail" | "screener" | "quarantine";

/** An encrypted attachment or offloaded body. */
export interface BlobRef {
  filename: string;
  content_type: string;
  size: number;
  sha256: string;
  key: string;
  /** Set when the body refers to this part as `cid:<id>` — an embedded image. */
  content_id?: string;
  /** True when the body displays this part itself rather than offering a file. */
  inline?: boolean;
}

export interface Verdict {
  disposition: Disposition;
  category?: string;
  confidence?: number;
  probs?: Record<string, number>;
  model?: string;
  reason?: string;
  sender_known?: boolean;
}

/** The sealed payload. Field names match the Go struct tags exactly. */
export interface MailPayload {
  message_id: string;
  date: string;
  from: string;
  to?: string[];
  cc?: string[];
  subject: string;
  in_reply_to?: string;
  /** The thread above this message, oldest first. */
  references?: string[];
  list_id?: string;
  unsubscribe?: string;
  text?: string;
  html?: string;
  /** Set when the body was too large to inline; fetch and decrypt separately. */
  text_ref?: BlobRef;
  html_ref?: BlobRef;
  attachments?: BlobRef[];
  verdict?: Verdict;
  /** True for a copy of a message this account sent. It belongs to no box. */
  sent?: boolean;
  /**
   * The address this delivery was for.
   *
   * The client cannot read the account registry, so this is how it learns which
   * address to send from. Mail sealed before 2026-09-25 does not carry it.
   */
  account?: string;
}

/** A decrypted message plus the wrap it came from. */
export interface Mail extends MailPayload {
  /** Gift wrap event id, stable and unique; used as the store key. */
  id: string;
  /** When the rumor was written, not the wrap's randomised timestamp. */
  sealedAt: number;
  /** Unset is email: every message sealed before Phase 7 is one. */
  origin?: "email" | "nostr";
  /** A DM sender's verified NIP-05, or their npub when they have none. */
  sender?: string;
  /** A DM sender's pubkey, hex, which is where a reply goes. */
  sender_pubkey?: string;
}

/** Both inner layers of a gift wrap, opened. */
export interface Unwrapped {
  seal: NostrEvent;
  rumor: NostrEvent;
}

/**
 * Unwrap a gift wrap.
 *
 * Two layers, each with its own counterparty: the wrap is encrypted to us by an
 * ephemeral key, and the seal inside it by the sender's real key. Deriving both
 * from the same pubkey is the mistake that looks like it works, because the
 * outer layer succeeds and the inner one fails with an opaque hmac error.
 *
 * Note the cost with a remote signer: this is **two** decrypt calls, so two
 * approvals' worth of round-trip per message. Plaintext is cached, so it is
 * paid once per message and never again.
 */
export async function openGiftWrap(wrap: NostrEvent, signer: MailSigner): Promise<Mail> {
  return mailOf(wrap.id, (await unwrap(wrap, signer)).rumor);
}

/** Open both layers without deciding what the rumor is. A DM and an email share this. */
export async function unwrap(wrap: NostrEvent, signer: MailSigner): Promise<Unwrapped> {
  const sealJson = await signer.nip44Decrypt(wrap.pubkey, wrap.content);
  const seal = JSON.parse(sealJson) as NostrEvent;

  const rumorJson = await signer.nip44Decrypt(seal.pubkey, seal.content);
  const rumor = JSON.parse(rumorJson) as NostrEvent;
  return { seal, rumor };
}

export function mailOf(wrapId: string, rumor: NostrEvent): Mail {
  if (rumor.kind !== KIND_MAIL) {
    throw new Error(`not mail: rumor kind ${rumor.kind}`);
  }
  const payload = JSON.parse(rumor.content) as MailPayload;
  return { ...payload, id: wrapId, sealedAt: rumor.created_at };
}

/** True when the failure is "this wrap is not for us", not "this wrap is broken". */
export function isNotOurs(err: unknown): boolean {
  const message = err instanceof Error ? err.message : String(err);
  return /invalid mac|invalid hmac|unknown encryption version/i.test(message);
}
